import { useQuery } from '@apollo/client';
import { READ_CHARGING_SESSIONS } from 'graphql/queries/readChargingSessions';
import { READ_PARKING_SESSIONS } from 'graphql/queries/readParkingSessions';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistoryStore } from 'stores/historyStore';
import { useRetryPaymentStore } from 'stores/retryPaymentStore';
import {
  ChargingPaymentState,
  Query,
  QueryReadHistoryChargingSessionsV2Args,
  QueryReadParkingSessionsArgs,
  ReadParkingSessionsInput,
  SessionPaymentState,
} from 'types/generatedSchemaTypes';
import { updateParkingQuery } from 'utils/parkUtils';
import useParkingHistoryFilter from './useParkingHistoryFilter';

interface FilterStates {
  isFreeTab: boolean;
  isCorporateAccountTab: boolean;
  isOtherTab: boolean;
}

const PAGE_SIZE = {
  FREE: 25,
  DEFAULT: 10,
  CHARGING: 100,
};

const getParkingPaymentState = (
  filterStates: FilterStates
): SessionPaymentState[] => {
  if (filterStates.isFreeTab) return ['Success'];
  if (filterStates.isCorporateAccountTab) return ['Billing', 'Billed'];
  return ['Failed', 'Retrying', 'Billing', 'Pending'];
};

const getChargingPaymentState = (
  filterStates: FilterStates
): ChargingPaymentState[] => {
  if (filterStates.isFreeTab) return ['Success'];
  if (filterStates.isCorporateAccountTab) return ['Billing', 'Billed'];
  return ['Failed', 'Retrying', 'Billing'];
};

const useReadStoppedSessionsForHistory = () => {
  const [currentPage, setCurrentPage] = useState(0);
  const { vehicle: vehicleFilter } = useParkingHistoryFilter();
  const parkingChargingHistoryFilter = useHistoryStore(
    (state) => state.parkingHistoryViewFilter
  );
  const retryStatuses = useRetryPaymentStore((s) => s.statuses);
  const { licensePlate } = vehicleFilter ?? {};

  const filterStates = useMemo<FilterStates>(
    () => ({
      isFreeTab: parkingChargingHistoryFilter === 'free',
      isCorporateAccountTab: parkingChargingHistoryFilter === 'billing',
      isOtherTab: parkingChargingHistoryFilter === 'other',
    }),
    [parkingChargingHistoryFilter]
  );

  const resetPage = useCallback(() => setCurrentPage(0), []);
  const nextPage = useCallback(() => setCurrentPage((prev) => prev + 1), []);

  const initialInput = useMemo<ReadParkingSessionsInput>(
    () => ({
      parkingState: ['Stopped'],
      accessDeviceValue: licensePlate?.plateText,
      sessionType: ['ANPR', 'SMS', 'START_STOP'],
      paymentState: getParkingPaymentState(filterStates),
      page: 0,
      size: filterStates.isFreeTab ? PAGE_SIZE.FREE : PAGE_SIZE.DEFAULT,
    }),
    [licensePlate?.plateText, filterStates]
  );

  const pagingInput = useMemo<ReadParkingSessionsInput>(
    () => ({ ...initialInput, page: currentPage }),
    [initialInput, currentPage]
  );

  const {
    data: allParkingSessions,
    loading,
    error,
    refetch,
    fetchMore,
  } = useQuery<Query, QueryReadParkingSessionsArgs>(READ_PARKING_SESSIONS, {
    variables: {
      input: initialInput,
    },
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });

  const { data: allChargingSessions } = useQuery<
    Query,
    QueryReadHistoryChargingSessionsV2Args
  >(READ_CHARGING_SESSIONS, {
    variables: {
      input: {
        licensePlate: licensePlate?.plateText,
        paymentStates: getChargingPaymentState(filterStates),
        page: 0,
        size: PAGE_SIZE.CHARGING,
      },
    },
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
    notifyOnNetworkStatusChange: true,
  });

  useEffect(() => {
    resetPage();
  }, [refetch, resetPage, initialInput]);

  useEffect(() => {
    if (currentPage !== 0) {
      fetchMore({
        variables: { input: pagingInput },
        updateQuery: updateParkingQuery,
      });
    }
  }, [currentPage, fetchMore, pagingInput]);

  const fetchMoreCallback = () => {
    if (!allParkingSessions?.readParkingSessions?.last) {
      nextPage();
    }
  };

  // Filtered Sessions Logic
  const parkingSessions = useMemo(() => {
    const sessions = allParkingSessions?.readParkingSessions?.content;

    if (!sessions) return [];

    return sessions.filter((item) => {
      // Corporate account sessions should go only to the Corporate Account tab
      if (
        !filterStates.isCorporateAccountTab &&
        item?.paymentType === 'CORPORATE_ACCOUNT'
      ) {
        return false;
      }

      if (filterStates.isFreeTab) return !item?.price?.totalAmount;
      if (filterStates.isOtherTab) return item?.poolingInfo?.pooled;
      if (filterStates.isCorporateAccountTab)
        return item?.paymentType === 'CORPORATE_ACCOUNT';

      // Optimistically hide sessions whose retry has just transitioned to
      // Success so that the session is removed from Failed payments, the receipt refetch may have not finished yet
      // but once it does, session appears under receipts tab.
      if (retryStatuses[item?.sessionId ?? ''] === 'Success') return false;

      // When a session is stopped and the payment process starts, the session paymentState goes from NotInitiated to Pending.
      // We should not show these in the failed history tab, as they are not yet paid. If there are no paymentRetries,
      // we know the payment process has just started. If, on the other hand, there are paymentRetries, we know the payment
      // has failed at some point.
      return !(item?.paymentState === 'Pending' && !item?.paymentRetries);
    });
  }, [
    filterStates.isFreeTab,
    filterStates.isOtherTab,
    filterStates.isCorporateAccountTab,
    allParkingSessions?.readParkingSessions?.content,
    retryStatuses,
  ]);

  const chargingSessions = useMemo(() => {
    const sessions =
      allChargingSessions?.readHistoryChargingSessionsV2?.content;

    if (!sessions) return [];

    if (filterStates.isFreeTab) {
      return sessions?.filter((item) => item?.priceInCents === 0);
    }
    // currently there is no idea on what we should show in "other" tab
    if (filterStates.isOtherTab) {
      return [];
    }
    return sessions;
  }, [
    filterStates.isFreeTab,
    filterStates.isOtherTab,
    allChargingSessions?.readHistoryChargingSessionsV2?.content,
  ]);

  return {
    parkingSessions,
    chargingSessions,
    sessionsLoading: loading,
    isLast: allParkingSessions?.readParkingSessions?.last,
    error,
    fetchMoreCallback,
    resetPage,
    sessionsRefetch: refetch,
  };
};

export default useReadStoppedSessionsForHistory;
